import { useState, useRef, useEffect } from "react"
import Typesense from "typesense"
import { orderBy } from "lodash"

const LANG = process.env.GATSBY_LANG || `sv`
const TYPESENSE_ENV = process.env.GATSBY_TYPESENSE_ENV || `production`
const TYPESENSE_KEY = process.env.GATSBY_TYPESENSE_KEY
const TYPESENSE_HOST =
  process.env.GATSBY_TYPESENSE_HOST || `https://ts.naturkartan.se`

const getTypesenseCollectionName = (name) => {
  return `${name}:${TYPESENSE_ENV}`
}

const Search = ({ data, query, children }) => {
  const perPage = 20
  const [status, setStatus] = useState(`idle`)
  const [page, setPage] = useState(1)
  const [results, setResults] = useState([])
  const typesenseRef = useRef(null)
  const hasNextPage = results.length > page * perPage

  const showNextPage = () => {
    if (hasNextPage) setPage((page) => page + 1)
  }

  const getTypesenseClient = () => {
    if (!typesenseRef.current)
      typesenseRef.current = new Typesense.Client({
        nodes: [{ url: TYPESENSE_HOST }],
        apiKey: TYPESENSE_KEY,
        connectionTimeoutSeconds: 2,
      })

    return typesenseRef.current
  }

  useEffect(() => {
    setResults([])
    setPage(1)
    setStatus(query ? `searching` : `idle`)

    if (query) {
      const indexes = {
        sites: data.sites?.nodes?.length && {
          params: {
            collection: getTypesenseCollectionName(`site`),
            query_by: `name_sv,name_${LANG},description_sv,description_${LANG}`,
            include_fields: `id,name_sv,name_${LANG},description_${LANG}`,
            filter_by: `id:=[${data.sites.nodes.map((s) => s.id).join(`,`)}]`,
          },
          normalizer: (hit) => ({
            title: hit[`name_${LANG}`] || hit.name_sv,
            description: hit[`description_${LANG}`],
          }),
        },
        articles: data.articles?.nodes?.length && {
          params: {
            collection: getTypesenseCollectionName(`article`),
            query_by: `title_sv,title_${LANG},body_sv,body_${LANG}`,
            include_fields: `id,title_sv,title_${LANG},body_${LANG}`,
            filter_by: `id:=[${data.articles.nodes
              .map((s) => s.id)
              .join(`,`)}]`,
          },
          normalizer: (hit) => ({
            title: hit[`title_${LANG}`] || hit.title_sv,
            description: hit[`body_${LANG}`],
          }),
        },
        news: data.news?.nodes?.length && {
          params: {
            collection: getTypesenseCollectionName(`news`),
            query_by: `title_sv,title_${LANG},body_sv,body_${LANG}`,
            include_fields: `id,title_sv,title_${LANG},body_${LANG}`,
            filter_by: `id:=[${data.news.nodes.map((s) => s.id).join(`,`)}]`,
          },
          normalizer: (hit) => ({
            title: hit[`title_${LANG}`] || hit.title_sv,
            description: hit[`body_${LANG}`],
          }),
        },
        events: data.events?.nodes?.length && {
          params: {
            collection: getTypesenseCollectionName(`event`),
            query_by: `title_sv,title_${LANG},body_sv,body_${LANG}`,
            include_fields: `id,title_sv,title_${LANG},body_${LANG}`,
            filter_by: `id:=[${data.events.nodes.map((s) => s.id).join(`,`)}]`,
          },
          normalizer: (hit) => ({
            title: hit[`title_${LANG}`] || hit.title_sv,
            description: hit[`body_${LANG}`],
          }),
        },
        tours: data.tours?.nodes?.length && {
          params: {
            collection: getTypesenseCollectionName(`miniguide`),
            query_by: `title_sv,title_${LANG},body_sv,body_${LANG}`,
            include_fields: `id,title_sv,title_${LANG},body_${LANG}`,
            filter_by: `id:=[${data.tours.nodes.map((s) => s.id).join(`,`)}]`,
          },
          normalizer: (hit) => ({
            title: hit[`title_${LANG}`] || hit.title_sv,
            description: hit[`body_${LANG}`],
          }),
        },
      }

      Object.keys(indexes).forEach((i) => !indexes[i] && delete indexes[i])

      getTypesenseClient()
        .multiSearch.perform(
          {
            searches: Object.values(indexes).map((i) => ({
              ...i.params,
              q: query,
            })),
          },
          {
            limit: 100,
            filter_by: process.env.GATSBY_BRANDSITES_INCLUDE_UNPUBLISHED
              ? ``
              : `published:true`,
          }
        )
        .then((response) => {
          let results = []

          Object.keys(indexes).map(
            (index, i) =>
              (results = results.concat(
                response.results[i].hits.map((hit) => {
                  const dataItem = data[index].nodes.find(
                    (n) => n.id == hit.document.id
                  )

                  const label =
                    index == `sites`
                      ? dataItem.theTypeData.title
                      : data.config.pages[index]?.title

                  return {
                    label,
                    path: dataItem.path,
                    image: dataItem.image,
                    type: index,
                    id: hit.document.id,
                    ranking: hit.text_match,
                    ...indexes[index].normalizer(hit.document),
                  }
                })
              ))
          )

          results = orderBy(results, [`ranking`], [`desc`])

          setResults(results)
          setStatus(`results`)
        })
        .catch((error) => {
          // console.log(error)
          setResults([])
          setStatus(`error`)
        })
    }
  }, [query])

  return children({
    status,
    results: results.slice(0, page * perPage),
    total: results.length,
    page,
    hasNextPage,
    showNextPage,
  })
}

export default Search
